import { Inbox, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";

export const PageHeader = ({ title, subtitle, actions, eyebrow }) => (
  <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between mb-8 syc-rise">
    <div>
      {eyebrow && <div className="text-[11px] uppercase tracking-[0.18em] text-[#258F00] font-semibold mb-2">{eyebrow}</div>}
      <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#17210f]" data-testid="page-title">{title}</h1>
      {subtitle && <p className="text-sm md:text-base text-[#5b6656] mt-2 max-w-2xl">{subtitle}</p>}
    </div>
    {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
  </div>
);

const STATUS = {
  activo: "bg-[#e9f7e1] text-[#1c6e00] border-[#bfe6a8]",
  inactivo: "bg-[#f1f1f1] text-[#666] border-[#ddd]",
  bloqueado: "bg-[#fdeaea] text-[#b42318] border-[#f5c2c0]",
  pendiente: "bg-[#fffbe0] text-[#8a6d00] border-[#f3e27a]",
  info: "bg-[#e8f1fd] text-[#1d4f91] border-[#bcd5f5]",
  simulado: "bg-[#fffbe0] text-[#8a6d00] border-[#f3e27a]",
};

export const StatusBadge = ({ status, label, testid }) => (
  <span data-testid={testid} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${STATUS[status] || STATUS.info}`}>
    <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
    {label || status}
  </span>
);

export const EmptyState = ({ title = "Sin registros", text, testid = "empty-state" }) => (
  <div data-testid={testid} className="flex flex-col items-center justify-center text-center py-14 px-6 text-[#5b6656]">
    <Inbox className="mb-3 text-[#9fb196]" size={32} />
    <div className="font-semibold text-[#17210f]">{title}</div>
    {text && <p className="text-sm mt-1 max-w-sm">{text}</p>}
  </div>
);

export const Forbidden = () => (
  <div data-testid="forbidden-page" className="syc-card p-10 text-center max-w-lg mx-auto mt-10">
    <ShieldAlert className="mx-auto mb-3 text-[#b42318]" size={36} />
    <h2 className="text-lg font-bold">Acceso no autorizado</h2>
    <p className="text-sm text-[#5b6656] mt-1">No tiene privilegios para ver esta sección. Si lo necesita, solicítelo al administrador.</p>
  </div>
);

export const Pager = ({ page, total, pageSize, onPage }) => {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-[#e4e8e1] text-sm text-[#5b6656]">
      <span data-testid="pager-info">{total} registros · página {page} de {pages}</span>
      <div className="flex gap-2">
        <Button data-testid="pager-prev-btn" variant="outline" size="sm" disabled={page <= 1} onClick={() => onPage(page - 1)}>Anterior</Button>
        <Button data-testid="pager-next-btn" variant="outline" size="sm" disabled={page >= pages} onClick={() => onPage(page + 1)}>Siguiente</Button>
      </div>
    </div>
  );
};

export const Field = ({ label, children, hint }) => (
  <label className="block space-y-1.5">
    <span className="text-[13px] font-semibold text-[#17210f]">{label}</span>
    {children}
    {hint && <span className="block text-xs text-[#5b6656]">{hint}</span>}
  </label>
);
